import React, {useRef, useState} from "react";
import GetDateFilter from "./GetDateFilter";
import getTimezoneOffset from "../../../../../utils/GetTimeZone";
import {SearchUserZone, SimpleSearch} from "../../../../../reduxToolkit/Search/SearchApi";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import CustomTh from "../../../../../components/table/CustomTh";
import CustomTd from "../../../../../components/table/CustomTd";
import { accessMethod, getAccessMethodById} from "../../../../../enums/accessMethodEnum";
import AccessMethodIcon from "../../../../../components/icons/AccessMethodIcon";
import CustomChip from "../../../../../components/CustomChip";
import {
    calculateTotalTimeInMinutes,
    formatMinutesToHoursMinutes,
    getDateFormat,
    getFullName
} from "../../../../../Apis/functions";
import TableFunction from "../../../../../components/table/TableFunction";
import CustomTooltip from "../../../../../components/CustomTooltip";
import {ATTENDANCE_MODES, getAttendanceColorByStatus} from "../../../Dining/Manage/Models/Model";
import {VIEW_TYPE} from "../../../../../enums/ViewType";
import {CRITERIA_MODEL, DEFAULT_LEFT, SEARCH_MODEL} from "../../../../../components/table/Model/TableModel";
import {ATTENDANCE_COLOR_MODEL, TYPE_MODEL_COLOR_MODEL} from '../Model/Model';
import { TimePickerChip } from "../../../../../components/buttons/IconButtons";

/**
 * @Email azapata@ibl.mx
 * @Author Ferro Zapata Arnulfo
 * @jobTitle Full Stack Developer
 * @createdAt 2025-10-07
 */

/**
 * Custom hook to manage and render the attendance table.
 *
 * Handles:
 * - Attendance data fetching, filtering, and search
 * - Table header and body generation
 * - Pagination, advanced search, and export preparation
 * - Color coding of attendance records
 *
 * @param {Object} props - Hook parameters
 * @param {string} props.selectedOption - Selected attendance mode (e.g., daily, weekly)
 * @param {string} props.searchElement - Search keyword for table filter
 * @param {Object} props.option - Additional table/search options
 * @param {Array} props.finalArray - Preloaded filtered attendance records
 * @param {string} props.color - Color theme for attendance indicators
 * @param {Function} props.setCriteria - Callback to set search criteria for advanced search
 * @param {Function} props.setBodyForExport - Callback to prepare export body data
 *
 *
 */
export const AttendanceTableApp =({
    selectedOption,
    searchElement,
    option,
    finalArray,
    color,
    setCriteria,
    setBodyForExport,
    modified,
    setModified
})=> {

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [results, setResults] = useState({ content: [], totalElements: 0 });
    const { getAttendanceDate } = GetDateFilter();
    const [attendanceStartDate, setAttendanceStartDate] = useState(getAttendanceDate(selectedOption).start);
    const [attendanceEndDate, setAttendanceEndDate] = useState( getAttendanceDate(selectedOption).end);
    const [tableId] = useState('tbl_aw_attendance_daily');
    const [searchId] = useState('txt_aw_search_attendance_daily');
    const leftPosition = DEFAULT_LEFT;
    const firstTime = useRef(true);
    const { user } = useSelector((state) => state.authenticatioauthennSlice);
    const userId = user?.data?.id;

    const renderAccessMethodCell = (accessMethod, methodName, deviceName, methodId) => (
        <div className="d-flex gap-2 align-items-center">
                {accessMethod?.icon && (
                    <CustomTooltip text={t(accessMethod.value)}>
                    <span><AccessMethodIcon className="icon-hover-secondary-500" AccessMethodId={methodId}/></span>
                    </CustomTooltip>
                )}
                {(methodName || deviceName)
                ? `${t(methodName?.toLowerCase() || "no_record")}${deviceName ? ` - ${deviceName}` : ''}`
                : t("no_record")
            }
            </div>
        );

    const getThead = () => {
        return [
            <CustomTh tableId={tableId} fixedXY={true} left={leftPosition} classes="first_head">{t("name")}</CustomTh>,
            <CustomTh tableId={tableId}>{t("entryAccessMethod")}</CustomTh>,
            <CustomTh tableId={tableId}>{t("entry_time")}</CustomTh>,
            <CustomTh tableId={tableId}>{t("exitAccessMethod")}</CustomTh>,
            <CustomTh tableId={tableId}>{t("exit_time")}</CustomTh>,
            <CustomTh tableId={tableId}/>,
            <CustomTh tableId={tableId}>{t("total_time")}</CustomTh>
        ]
    };

    const getSimpleBody = (results, tableId, color, t) => {
        return results.content.map((member) => {
            const attendance = Array.isArray(member.attendances) ? member.attendances[0] : null;

            const entryAccessMethod = getAccessMethodById(attendance?.entryAccessMethodId);
            const exitAccessMethod = getAccessMethodById(attendance?.exitAccessMethodId);

            const totalTimeMinutes =
                attendance?.entryDate && attendance?.exitDate
                    ? calculateTotalTimeInMinutes(attendance.entryDate, attendance.exitDate)
                    : null;

            return [
                <CustomTd tableId={tableId} fixed={true} left={leftPosition} classes='first-column'>
                
                        <CustomTooltip
                            text={
                                member?.fullName ||
                                `${getFullName(member?.userName, member?.userLastName, member?.userSecondLastName)}`
                            }
                            classes={`fs-14px font-color-general-950 ibl-font-w-600 text-ellipsis`}
                            showIfTruncated={true}
                        />
                 
                </CustomTd>,
                <CustomTd tableId={tableId}>
                    {renderAccessMethodCell(
                        entryAccessMethod,
                        accessMethod[attendance?.entryAccessMethodId],
                        attendance?.entryDeviceName,
                        attendance?.entryAccessMethodId,
                        accessMethod[attendance?.entryAccessMethodId],
                    )}
                </CustomTd>,
                <CustomTd tableId={tableId}>
                    <CustomChip
                        background={getAttendanceColorByStatus(
                            attendance?.entryDateStatus,
                            color,
                            ATTENDANCE_COLOR_MODEL,
                        )}
                        fontColor={getAttendanceColorByStatus(
                            attendance?.entryDateStatus,
                            color,
                            ATTENDANCE_COLOR_MODEL,
                            TYPE_MODEL_COLOR_MODEL.TXT_COLOR,
                        )}
                        legend={getDateFormat(attendance?.entryDate) || t('no_record')}
                    />
                </CustomTd>,
                <CustomTd tableId={tableId}>
                    {renderAccessMethodCell(
                        exitAccessMethod,
                        accessMethod[attendance?.exitAccessMethodId],
                        attendance?.exitDeviceName,
                        attendance?.exitAccessMethodId,
                        accessMethod[attendance?.exitAccessMethodId],
                    )}
                </CustomTd>,
                <CustomTd tableId={tableId}>
                    <CustomChip
                        background={getAttendanceColorByStatus(
                            attendance?.exitDateStatus,
                            color,
                            ATTENDANCE_COLOR_MODEL,
                        )}
                        fontColor={getAttendanceColorByStatus(
                            attendance?.exitDateStatus,
                            color,
                            ATTENDANCE_COLOR_MODEL,
                            TYPE_MODEL_COLOR_MODEL.TXT_COLOR,
                        )}
                        legend={getDateFormat(attendance?.exitDate) || t('no_record')}
                    />
                </CustomTd>,
                <CustomTd tableId={tableId}>=</CustomTd>,
                <CustomTd tableId={tableId}>
                    <CustomChip
                        icon={
                            totalTimeMinutes ? (
                                <TimePickerChip
                                    fill={getAttendanceColorByStatus(
                                        attendance?.totalTimeStatus,
                                        color,
                                        ATTENDANCE_COLOR_MODEL,
                                        TYPE_MODEL_COLOR_MODEL.TXT_COLOR,
                                    )}
                                />
                            ) : undefined
                        }
                        background={getAttendanceColorByStatus(
                            attendance?.totalTimeStatus,
                            color,
                            ATTENDANCE_COLOR_MODEL,
                        )}
                        fontColor={getAttendanceColorByStatus(
                            attendance?.totalTimeStatus,
                            color,
                            ATTENDANCE_COLOR_MODEL,
                            TYPE_MODEL_COLOR_MODEL.TXT_COLOR,
                        )}
                        legend={
                            totalTimeMinutes != null
                                ? formatMinutesToHoursMinutes(attendance.totalTime)
                                : t('no_record')
                        }
                        className='text-lowercase'
                    />
                </CustomTd>,
            ];
        });
    };
    const getAdvancedBody = (results, tableId, color, t) => {
        return results.content.map((member) => {
            const attendance = member;

            const entryAccessMethod = getAccessMethodById(attendance?.entryAccessMethodId);
            const exitAccessMethod = getAccessMethodById(attendance?.exitAccessMethodId);

            return [
                <CustomTd tableId={tableId} classes="first-column">
                    {member?.fullName || `${member?.userName} ${member?.userLastName} ${member?.userSecondLastName || ''}`}
                </CustomTd>,
                <CustomTd tableId={tableId}>
                    {renderAccessMethodCell(
                        entryAccessMethod,
                        accessMethod[attendance?.entryAccessMethodId],
                        attendance?.entryDeviceName,
                        attendance?.entryAccessMethodId,
                        accessMethod[attendance?.entryAccessMethodId],
                    )}
                </CustomTd>,
                <CustomTd tableId={tableId}>
                    <CustomChip
                        background={getAttendanceColorByStatus(attendance?.entryDateStatus, color, ATTENDANCE_COLOR_MODEL)}
                        fontColor={getAttendanceColorByStatus(attendance?.entryDateStatus, color, ATTENDANCE_COLOR_MODEL, TYPE_MODEL_COLOR_MODEL.TXT_COLOR)}
                        legend={getDateFormat(attendance?.entryDate, true) || t("no_record")}
                    />
                </CustomTd>,
                <CustomTd tableId={tableId}>
                    {renderAccessMethodCell(
                        exitAccessMethod,
                        accessMethod[attendance?.exitAccessMethodId],
                        attendance?.exitDeviceName,
                        attendance?.exitAccessMethodId,
                        accessMethod[attendance?.exitAccessMethodId],
                    )}
                </CustomTd>,
                <CustomTd tableId={tableId}>
                    <CustomChip
                        background={getAttendanceColorByStatus(attendance?.exitDateStatus, color, ATTENDANCE_COLOR_MODEL)}
                        fontColor={getAttendanceColorByStatus(attendance?.exitDateStatus, color, ATTENDANCE_COLOR_MODEL, TYPE_MODEL_COLOR_MODEL.TXT_COLOR)}
                        legend={getDateFormat(attendance?.exitDate, true) || t("no_record")}
                    />
                </CustomTd>,
                <CustomTd tableId={tableId}>=</CustomTd>,
                <CustomTd tableId={tableId}>
                    <CustomChip
                        background={getAttendanceColorByStatus(attendance?.totalTimeStatus, color, ATTENDANCE_COLOR_MODEL)}
                        fontColor={getAttendanceColorByStatus(attendance?.totalTimeStatus, color, ATTENDANCE_COLOR_MODEL, TYPE_MODEL_COLOR_MODEL.TXT_COLOR)}
                        legend={
                            attendance?.totalTime != null
                                ? formatMinutesToHoursMinutes(attendance.totalTime)
                                : t('no_record')
                        }
                        className=' text-lowercase'
                    />
                </CustomTd>,
            ];
        });
    };

    const getTbody = () => {
        if (!results?.content?.length) return { body: [], data: [] };

        const body = finalArray?.length > 0
                ? getAdvancedBody(results, tableId, color, t)
                : getSimpleBody(results, tableId, color, t);

        return { body, data: results };
    };

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page, { searchElement }) => {
        // On mount with an active simple search, SearchSimple already triggers the request. Avoid duplicating it.
        if (firstTime.current) {
            firstTime.current = false;
            if (searchElement) return;
        }

        if(finalArray.length > 0) return;

        const body = SEARCH_MODEL.setObject({
            page,
            size: rowsPerPage,
            attendanceTimeZone: getTimezoneOffset(),
            attendanceStartDate,
            attendanceEndDate,
            attendanceSearchType: ATTENDANCE_MODES.daily,
            key: searchElement,
            model: option
        });
        setBodyForExport({ ...body,  userId });

        dispatch(SimpleSearch({ body })).then(({ payload: { data: { data, success } } }) => {
                if (success) {
                    setResults(data);
                }
        });
    }

    /**
     * Executes a paginated search request using advanced criteria.
     *
     * Builds the criteria list based on the advanced search (finalArray),
     * appends user criteria, and dispatches the search action through Redux.
     * Updates the results and resets the selected list once the data is returned.
     */
    const getDataAdvancedSearch = (page) => {
        const body = SEARCH_MODEL.setObject({
            page,
            size: rowsPerPage,
            attendanceTimeZone: getTimezoneOffset(),
            attendanceStartDate,
            attendanceEndDate,
            attendanceSearchType: selectedOption,
            rootGroup: CRITERIA_MODEL.set(finalArray),
            model: option,
            userToFindId: userId
        });

        setCriteria(body);
        dispatch(SearchUserZone({ option, body })).then(({ payload: { data: { data } } }) => {
                setResults(data);
        });
    };

    const {thead, tbody, pagination, page, rowsPerPage } = TableFunction({
        data: results,
        getThead,
        getTbody,
        view: VIEW_TYPE.LIST.name,
        labelRowsPerPage: 'attendance_per_page',
        fixedPagination: false,
        hasContainer:true,
        searchElement, //New
        getData: getData,//New
        modified,//New
        setModified,//New
        getDataAdvancedSearch,//New - apply only with advanced search
        finalArray//New - apply only with advanced search

    });


    return {
        attendanceStartDate,
        attendanceEndDate,
        page,
        rowsPerPage,
        tableId,
        thead,
        tbody,
        color,
        t,
        pagination,
        searchId,
        setResults,
        selectedDate,
        setSelectedDate,
        getData

    };
};
